import { create } from 'zustand'
import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc,
  serverTimestamp, arrayUnion, arrayRemove, deleteField,
} from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { db, getStorageInstance } from '../firebase'
import { useAuthStore } from './auth'
import { useImpersonationStore } from './impersonation'
import { getErrorMessage } from '../types/firebase'
import { normalizeProfile, type SafetyProfile, type ProfileFormData, type LinkedUser, type EmergencyContact } from '../types/profiles'

interface ProfilesState {
  profiles: SafetyProfile[]
  loading: boolean
  loaded: boolean
  loadedForUid: string | null
  error: string | null

  getById: (id: string) => SafetyProfile | undefined
  fetchProfiles: () => Promise<void>
  fetchProfile: (id: string) => Promise<SafetyProfile | null>
  createProfile: (data: ProfileFormData) => Promise<string>
  updateProfile: (id: string, data: Partial<ProfileFormData>) => Promise<void>
  patchEmergencyContacts: (ownerUid: string, profileId: string, contacts: EmergencyContact[]) => Promise<void>
  deleteProfile: (id: string) => Promise<void>
  uploadProfilePhoto: (id: string, file: File) => Promise<string>
  removeProfilePhoto: (id: string) => Promise<void>
  fetchLinkedUsers: (uids: string[]) => Promise<LinkedUser[]>
  unlinkCarer: (profileId: string, carerUid: string, carerEmail: string) => Promise<void>
}

function profilesRef(uid: string) {
  return collection(db, 'users', uid, 'profiles')
}

export const useProfilesStore = create<ProfilesState>()((set, get) => ({
  profiles: [],
  loading: false,
  loaded: false,
  loadedForUid: null,
  error: null,

  getById: (id) => get().profiles.find((p) => p.id === id),

  fetchProfiles: async () => {
    const auth = useAuthStore.getState()
    const impersonatedUid = useImpersonationStore.getState().targetUid
    const uid = impersonatedUid ?? auth.uid()
    if (!uid) return
    if (get().loading) return
    if (get().loaded && get().loadedForUid === uid && get().profiles.length > 0) return

    set((s) => ({
      profiles: s.loadedForUid && s.loadedForUid !== uid ? [] : s.profiles,
      loading: true,
      loaded: false,
      error: null,
    }))
    try {
      const snapshot = await getDocs(profilesRef(uid))
      const own = snapshot.docs.map((d) => normalizeProfile(d.id, d.data() as Record<string, unknown>))

      // Skip managed profiles when impersonating — show only the target user's own profiles
      const managed: SafetyProfile[] = []
      if (!impersonatedUid) {
        const managedRefs = (auth.userDoc?.managedProfiles ?? []).filter((r) => r.ownerUid !== uid)
        for (const r of managedRefs) {
          try {
            const snap = await getDoc(doc(db, 'users', r.ownerUid, 'profiles', r.profileId))
            if (snap.exists()) {
              const p = normalizeProfile(snap.id, snap.data() as Record<string, unknown>)
              p.ownerUid = r.ownerUid
              managed.push(p)
            }
          } catch (err: unknown) {
            console.warn('[profiles] skipped bad ref:', r, err)
          }
        }
      }

      const all = [...own, ...managed].sort((a, b) => a.fullName.localeCompare(b.fullName))
      if (typeof Image !== 'undefined') {
        all.forEach((p) => { if (p.photoUrl) { const img = new Image(); img.src = p.photoUrl } })
      }
      set({ profiles: all, loaded: true, loadedForUid: uid })
    } catch (err: unknown) {
      set({ error: getErrorMessage(err) })
      console.error('[profiles] fetchProfiles failed:', err)
    } finally {
      set({ loading: false })
    }
  },

  fetchProfile: async (id) => {
    const auth = useAuthStore.getState()
    const uid = auth.uid()
    if (!uid) return null
    try {
      const ownSnap = await getDoc(doc(db, 'users', uid, 'profiles', id))
      if (ownSnap.exists()) return normalizeProfile(ownSnap.id, ownSnap.data() as Record<string, unknown>)

      const managedRef = auth.userDoc?.managedProfiles?.find((r) => r.profileId === id)
      if (!managedRef) return null

      const managedSnap = await getDoc(doc(db, 'users', managedRef.ownerUid, 'profiles', id))
      if (!managedSnap.exists()) return null
      const profile = normalizeProfile(managedSnap.id, managedSnap.data() as Record<string, unknown>)
      profile.ownerUid = managedRef.ownerUid
      return profile
    } catch (err: unknown) {
      console.error('[profiles] fetchProfile failed:', err)
      return null
    }
  },

  createProfile: async (data) => {
    const uid = useAuthStore.getState().uid()
    if (!uid) throw new Error('Not authenticated')
    const docData = { ...data, linkedUids: [uid], createdAt: serverTimestamp(), updatedAt: serverTimestamp() }
    try {
      const ref = await addDoc(profilesRef(uid), docData)
      const newProfile = normalizeProfile(ref.id, docData as Record<string, unknown>)
      set((s) => ({
        profiles: [...s.profiles, newProfile].sort((a, b) => a.fullName.localeCompare(b.fullName)),
        loaded: true,
        loadedForUid: uid,
      }))
      return ref.id
    } catch (err: unknown) {
      console.error('[profiles] createProfile failed:', getErrorMessage(err))
      throw err
    }
  },

  updateProfile: async (id, data) => {
    const uid = useAuthStore.getState().uid()
    if (!uid) throw new Error('Not authenticated')
    const { linkedUids: _ignored, ...rest } = data
    try {
      await updateDoc(doc(db, 'users', uid, 'profiles', id), {
        ...rest,
        linkedUids: arrayUnion(uid),
        updatedAt: serverTimestamp(),
      })
    } catch (err: unknown) {
      console.error('[profiles] updateProfile failed:', getErrorMessage(err))
      throw err
    }
    set((s) => ({
      profiles: s.profiles.map((p) =>
        p.id === id
          ? { ...p, ...rest, linkedUids: p.linkedUids.includes(uid) ? p.linkedUids : [...p.linkedUids, uid] }
          : p,
      ),
    }))
  },

  patchEmergencyContacts: async (ownerUid, profileId, contacts) => {
    try {
      await updateDoc(doc(db, 'users', ownerUid, 'profiles', profileId), {
        emergencyContacts: contacts,
        updatedAt: serverTimestamp(),
      })
    } catch (err: unknown) {
      console.error('[profiles] patchEmergencyContacts failed:', getErrorMessage(err))
      throw err
    }
    set((s) => ({
      profiles: s.profiles.map((p) => (p.id === profileId ? { ...p, emergencyContacts: contacts } : p)),
    }))
  },

  deleteProfile: async (id) => {
    const uid = useAuthStore.getState().uid()
    if (!uid) throw new Error('Not authenticated')
    try {
      await deleteDoc(doc(db, 'users', uid, 'profiles', id))
    } catch (err: unknown) {
      console.error('[profiles] deleteProfile failed:', getErrorMessage(err))
      throw err
    }
    set((s) => ({ profiles: s.profiles.filter((p) => p.id !== id), loaded: true, loadedForUid: uid }))
  },

  uploadProfilePhoto: async (id, file) => {
    const uid = useAuthStore.getState().uid()
    if (!uid) throw new Error('Not authenticated')
    const { default: imageCompression } = await import('browser-image-compression')
    const compressed = await imageCompression(file, { maxSizeMB: 0.1, maxWidthOrHeight: 600, useWebWorker: true })
    const path = `profiles/${uid}/${id}/photo.jpg`
    const ref = storageRef(await getStorageInstance(), path)
    await uploadBytes(ref, compressed, { contentType: 'image/jpeg' })
    const url = await getDownloadURL(ref)
    await get().updateProfile(id, { photoUrl: url })
    return url
  },

  removeProfilePhoto: async (id) => {
    const uid = useAuthStore.getState().uid()
    if (!uid) throw new Error('Not authenticated')
    const path = `profiles/${uid}/${id}/photo.jpg`
    try {
      await deleteObject(storageRef(await getStorageInstance(), path))
    } catch (err: unknown) {
      console.warn('[profiles] removeProfilePhoto already deleted:', err)
    }
    try {
      await updateDoc(doc(db, 'users', uid, 'profiles', id), { photoUrl: deleteField(), updatedAt: serverTimestamp() })
    } catch (err: unknown) {
      console.error('[profiles] removeProfilePhoto update failed:', getErrorMessage(err))
      throw err
    }
    set((s) => ({
      profiles: s.profiles.map((p) => (p.id === id ? { ...p, photoUrl: undefined } : p)),
    }))
  },

  fetchLinkedUsers: async (uids) => {
    if (!uids.length) return []
    const results: LinkedUser[] = []
    for (const uid of uids) {
      try {
        const snap = await getDoc(doc(db, 'users', uid))
        if (snap.exists()) {
          const d = snap.data()
          results.push({
            uid,
            displayName: String(d.displayName || 'Unknown'),
            email: String(d.email || ''),
            photoURL: d.photoURL ? String(d.photoURL) : null,
            phone: String(d.contactInfo?.phone || ''),
          })
        }
      } catch (err: unknown) {
        console.warn('[profiles] fetchLinkedUsers skip:', err)
      }
    }
    return results
  },

  unlinkCarer: async (profileId, carerUid, carerEmail) => {
    const uid = useAuthStore.getState().uid()
    if (!uid) throw new Error('Not authenticated')
    try {
      await updateDoc(doc(db, 'users', uid, 'profiles', profileId), {
        linkedUids: arrayRemove(carerUid),
        sharedWithEmails: arrayRemove(carerEmail.toLowerCase()),
      })
    } catch (err: unknown) {
      console.error('[profiles] unlinkCarer failed:', getErrorMessage(err))
      throw err
    }
    set((s) => ({
      profiles: s.profiles.map((p) =>
        p.id === profileId
          ? {
              ...p,
              linkedUids: p.linkedUids.filter((u) => u !== carerUid),
              sharedWithEmails: p.sharedWithEmails.filter((e) => e !== carerEmail.toLowerCase()),
            }
          : p,
      ),
    }))
  },
}))
